import React, {useEffect, useRef} from 'react';
import {Platform, View} from 'react-native';
import Animated, {useAnimatedStyle} from 'react-native-reanimated';
import {BottomTabBar, type BottomTabBarProps} from '@react-navigation/bottom-tabs';

import {useScrollCollapseContext} from '../../context/ScrollCollapseContext';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';

export function AnimatedTabBar(props: BottomTabBarProps) {
  const {isWebDesktopScreen} = useWebScreenDimensions();

  // Desktop doesn't use bottom tabs (Stack navigator), but guard anyway.
  // Native mobile: tab bar stays fixed (no scroll collapse).
  if (isWebDesktopScreen || Platform.OS !== 'web') {
    return (
      <View testID="tab-bar">
        <BottomTabBar {...props} />
      </View>
    );
  }

  return <AnimatedTabBarInner {...props} />;
}

function AnimatedTabBarInner(props: BottomTabBarProps) {
  const {accumulatedDown, accumulatedUp, TAB_BAR_HEIGHT} = useScrollCollapseContext();

  // Reset accumulators on tab change so tab bar starts visible.
  // scrollY/prevScrollY restore is handled by useFocusEffect in the scroll handler hooks
  // (useScrollCollapseHandler / useScrollCollapseJSHandler). We only reset accumulators here.
  const activeIndex = props.state.index;
  const prevIndex = useRef(activeIndex);
  useEffect(() => {
    if (prevIndex.current !== activeIndex) {
      prevIndex.current = activeIndex;
      accumulatedUp.set(TAB_BAR_HEIGHT);
      accumulatedDown.set(0);
    }
  }, [activeIndex, accumulatedUp, accumulatedDown, TAB_BAR_HEIGHT]);

  const tabBarStyle = useAnimatedStyle(() => {
    'worklet';
    const showProgress = accumulatedUp.get() / TAB_BAR_HEIGHT;
    const hideProgress = accumulatedDown.get() / TAB_BAR_HEIGHT;

    const translateY =
      hideProgress > showProgress
        ? hideProgress * TAB_BAR_HEIGHT
        : (1 - showProgress) * TAB_BAR_HEIGHT;

    return {
      transform: [{translateY: Math.max(0, translateY)}],
    };
  });

  return (
    <Animated.View style={[{position: 'absolute', bottom: 0, left: 0, right: 0}, tabBarStyle]}>
      <BottomTabBar {...props} />
    </Animated.View>
  );
}
